import React, { ReactNode } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { ButtonSpinner } from './LoadingSpinner';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm?: () => void;
    title: string;
    message?: string;
    content?: ReactNode;
    confirmText?: string | ReactNode;
    cancelText?: string;
    type?: 'info' | 'warning' | 'error';
    disabled?: boolean;
    showTextField?: boolean;
    textFieldValue?: string;
    onTextFieldChange?: (value: string) => void;
    confirmLoading?: boolean;
}

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message = '',
    content,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    type = 'info',
    disabled = false,
    showTextField = false,
    textFieldValue = '',
    onTextFieldChange,
    confirmLoading = false
}) => {
    const { theme } = useTheme();

    if (!isOpen) return null;

    const getTypeStyles = () => {
        switch (type) {
            case 'warning':
                return {
                    backgroundColor: theme.colors.SURFACE,
                    borderColor: theme.colors.WARNING,
                    color: theme.colors.TEXT,
                    buttonColor: theme.colors.WARNING
                };
            case 'error':
                return {
                    backgroundColor: theme.colors.SURFACE,
                    borderColor: theme.colors.ERROR,
                    color: theme.colors.TEXT,
                    buttonColor: theme.colors.ERROR
                };
            default:
                return {
                    backgroundColor: theme.colors.SURFACE,
                    borderColor: theme.colors.PRIMARY,
                    color: theme.colors.TEXT,
                    buttonColor: theme.colors.PRIMARY
                };
        }
    };

    const typeStyles = getTypeStyles();

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: typeStyles.backgroundColor,
                border: `1px solid ${typeStyles.borderColor}`,
                borderRadius: theme.borderRadius.MEDIUM,
                padding: theme.spacing.LARGE,
                maxWidth: '90%',
                width: '400px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
                <h2 style={{
                    margin: 0,
                    marginBottom: theme.spacing.MEDIUM,
                    color: typeStyles.color,
                    fontSize: '1.25em',
                    fontWeight: 'bold'
                }}>
                    {title}
                </h2>
                <div style={{
                    margin: 0,
                    marginBottom: theme.spacing.LARGE,
                    color: typeStyles.color
                }}>
                    {content !== undefined
                        ? content
                        : message.split('\n').map((line, i) => (
                            <p key={i} style={{ margin: i === 0 ? '0 0 0.5em 0' : '0.5em 0' }}>
                                {line}
                            </p>
                        ))}
                </div>
                {showTextField && (
                    <textarea
                        value={textFieldValue}
                        onChange={(e) => onTextFieldChange?.(e.target.value)}
                        placeholder="Type here"
                        style={{
                            width: '100%',
                            height: '100px',
                            marginBottom: theme.spacing.MEDIUM,
                            padding: theme.spacing.SMALL,
                            borderRadius: theme.borderRadius.SMALL,
                            border: `1px solid ${theme.colors.BORDER}`,
                            backgroundColor: theme.colors.BACKGROUND,
                            color: theme.colors.TEXT
                        }}
                    />
                )}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: theme.spacing.MEDIUM
                }}>
                    {onConfirm && (
                        <button
                            onClick={onClose}
                            style={{
                                padding: `${theme.spacing.MEDIUM}px ${theme.spacing.LARGE}px`,
                                backgroundColor: 'transparent',
                                border: `1px solid ${theme.colors.BORDER}`,
                                borderRadius: theme.borderRadius.SMALL,
                                color: theme.colors.TEXT,
                                cursor: 'pointer',
                                fontSize: '1.1em',
                                fontWeight: '500'
                            }}
                            disabled={false}
                        >
                            {cancelText}
                        </button>
                    )}
                    <button
                        onClick={onConfirm || onClose}
                        style={{
                            padding: `${theme.spacing.MEDIUM}px ${theme.spacing.LARGE}px`,
                            backgroundColor: disabled ? theme.colors.BORDER : typeStyles.buttonColor,
                            border: 'none',
                            borderRadius: theme.borderRadius.SMALL,
                            color: theme.colors.WHITE,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled ? 0.5 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '100px',
                            fontSize: '1.1em',
                            fontWeight: '500',
                            boxShadow: disabled ? 'none' : undefined,
                            transition: 'background 0.2s, opacity 0.2s, color 0.2s'
                        }}
                        disabled={disabled}
                    >
                        {confirmLoading ? (
                            <ButtonSpinner size={20} color={theme.colors.WHITE} />
                        ) : (
                            onConfirm ? confirmText : 'OK'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Modal; 